import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { DataSetConfigurationModalComponent } from '../../components/modals/data-set-configuration-modal/data-set-configuration-modal.component';
import { Comparison } from '../../models/comparison';
import { Corpus, RepresentationSet, SharedState } from '../../models/corpus';
import { Custodian } from '../../models/custodian';
import { DynamicFolder } from '../../models/dynamic-folder';
import {
    Endpoint, EntityType, ExternalAreaModalType, MessageType, ModalTargetType, RemoveRestoreDocsOperationType,
    SaveToRemoveFromOperationType, SearchType, TagOperationType, UpdateStatusType, ReviewSystem,
    TemplateOperationType,
    SettingsType
} from '../../models/enums';
import { ExportStream } from '../../models/export-stream';
import { ExportVolume, ExportVolumeDedupeState } from '../../models/export-volume';
import { MediaID } from '../../models/mediaid';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { NavigationInfo, NavigationTab } from '../../models/navigation-info';
import { ActionType, ObjectActionListType, Permissions } from '../../models/permission';
import { Profile } from '../../models/profile';
import { ExportComparisonReport } from '../../models/report';
import { SavedSearch } from '../../models/saved-searches';
import { SearchResult } from '../../models/search-result';
import { isArray, isNullOrUndefined, isNumber, isString, isUndefined } from '../../models/utils';
import { View, ViewType } from '../../models/view';
import { Workflow, WorkflowStep } from '../../models/workflow';
import { WorkingSet } from '../../models/working-set';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { TreeDataLoaderService } from '../../services/tree/treeDataLoader.service';
import { XMLService } from '../../services/xml/xml.service';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { CustomSelectItem } from '../custom-select/custom-select.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';
import { AddSharedDataSetModalComponent } from '../modals/add-shared-data-set-modal/add-shared-data-set-modal.component';
import { AddToRemoveFromModalComponent, AddToRemoveFromModalData } from '../modals/add-to-remove-from-modal/add-to-remove-from-modal.component';
import { CopyToDocStorageModalComponent } from '../modals/copy-to-doc-storage-modal/copy-to-doc-storage-modal.component';
import { CopyToLoadFromExternalAreaModalComponent } from '../modals/copy-to-load-from-external-area-modal/copy-to-load-from-external-area-modal.component';
import { CreateDatasetFromSearchResultModalComponent } from '../modals/create-dataset-from-search-result-modal/create-dataset-from-search-result-modal.component';
import { CreateEditComparisonModalComponent } from '../modals/create-edit-comparison-modal/create-edit-comparison-modal.component';
import { CreateEditEntityModalComponent } from '../modals/create-edit-entity-modal/create-edit-entity-modal.component';
import { CreateManifestModalComponent } from '../modals/create-manifest/create-manifest.component';
import { CreateNearDuplicateMetadataModalComponent } from '../modals/create-near-duplicate-metadata-modal/create-near-duplicate-metadata-modal.component';
import { CreateSampleModalComponent } from '../modals/create-sample-modal/create-sample-modal.component';
import { DataSetModalComponent } from '../modals/data-set-modal/data-set-modal.component';
import { DeleteEntityModalComponent } from '../modals/delete-entity-modal/delete-entity-modal.component';
import { EditBucketModalComponent, EditMode } from '../modals/edit-bucket/edit-bucket-modal.component';
import { ExportDuplicatesVolumeModalComponent } from '../modals/export-duplicates-volume-modal/export-duplicates-volume-modal.component';
import { ExportModalComponent } from '../modals/export-modal/export-modal.component';
import { ExtractOffice365DataModalComponent, ExtractOffice365DataModalData } from '../modals/extract-office-365-data-modal/extract-office-365-data-modal.component';
import { FindInTreeModalComponent } from '../modals/find-in-tree-modal/find-in-tree-modal.component';
import { GenerateLoadFileModalComponent } from '../modals/generate-load-file-modal/generate-load-file-modal.component';
import { GenericPickerModalComponent } from '../modals/generic-picker-modal/generic-picker-modal.component';
import { NewLoadFileModalComponent } from '../modals/new-load-file-modal/new-load-file-modal.component';
import { OcrSettingsComponent, OCRSettingsModalData } from '../modals/ocr-settings/ocr-settings.component';
import { ReindexDataSetModalComponent } from '../modals/reindex-data-set-modal/reindex-data-set-modal.component';
import { RemoveRestoreDocsModalComponent, RemoveRestoreDocsModalData } from '../modals/remove-restore-docs-modal/remove-restore-docs-modal.component';
import { ReprocessModalComponent, ReprocessModalData } from '../modals/reprocess-modal/reprocess-modal-modal.component';
import { TagsModalComponent, TagsModalData } from '../modals/tags-modal/tags-modal.component';
import { TargetPickerModalComponent } from '../modals/target-picker-modal/target-picker-modal.component';
import { UpdateExportVolumeModalComponent } from '../modals/update-export-volume-modal/update-export-volume-modal.component';
import { UpdateMetadataModalComponent } from '../modals/update-metadata-modal/update-metadata-modal.component';
import { WordListModalComponent } from '../modals/word-list-modal/word-list-modal.component';
import { ResizerComponent } from '../resizer/resizer.component';
import { SearchComponent } from '../search/search.component';
import { TargetPickerTreeType } from '../target-picker-tree/target-picker-tree.component';
import { AnalyticComparisonMenu, AnalyticMenu, AnalyticProfileMenu } from './menus/analytic-menu';
import { DashboardMenu } from './menus/dashboard-menu';
import { ExportMenu } from './menus/export-menu';
import { ImportMenu } from './menus/import-menu';
import { ProjectDataCustodianMenu, ProjectDataFolderMenu, ProjectDataMediaIDsMenu, ProjectDataMenu, ProjectDataTagMenu, ProjectDataBatchesMenu } from './menus/project-data-menu';
import { SearchSavedSearchMenu, SearchSearchHistoryMenu } from './menus/search-menu';
import { WorkflowMenu } from './menus/workflow-menu';
import { Batch } from '../../models/batch';
import { DocumentFormat } from '../../models/dr-document';
import { DownloadAsPDFStampModalComponent } from '../modals/download-as-pdf-stamp-modal/download-as-pdf-stamp-modal.component';
import { Type } from '../../models/connector';
import { DrObjectType } from '../../models/dr-object';
import { SaveAsLoadFromTemplateComponent } from '../modals/settings-modal/save-as-load-from-template/save-as-load-from-template.component';
import { ExportToReviewModalComponent } from '../modals/export-to-review-modal/export-to-review-modal.component';
import { DiscardPileMenu } from './menus/discard-pile-menu';
import { DiscardPileLogComponent } from '../modals/discard-pile-log-modal/discard-pile-log-modal.component';
import { MetadataService } from '../../services/metadata/metadata.service';

@Component({
    standalone: false,
    selector: 'res-main-content',
    templateUrl: './main-content.component.html',
    styleUrls: ['./main-content.component.scss']
})
export class MainContentComponent implements OnInit, OnDestroy {
    @ViewChild('dashboardTree') dashboardTree: GenericTreeComponent;
    @ViewChild('navigationTree', { static: true }) navigationTree: GenericTreeComponent;
    @ViewChild(BreadcrumbComponent, { static: true }) breadcrumbComponent: BreadcrumbComponent;
    @ViewChildren(ResizerComponent) resizers: QueryList<ResizerComponent>;
    @ViewChild(SearchComponent, { static: true }) searchComponent: SearchComponent;
    @ViewChild('searchBarResizer', { static: true }) searchBarResizer: ResizerComponent;

    isBusy = false;
    isBusyMessage: string;
    simpleSearchSize = 120;
    hasWorkBasketAccess: boolean;
    hasComparisionPermissions: boolean;
    hasAccessToReload: boolean;

    private entityChangedEvent: Subscription;
    private clearPendingNavigationEvent: Subscription;
    private pendingNavigation: any;
    private pendingNavigationTab: any;
    private pendingChildNavigation: any;  // Used to navigate down a second level - currently only for export comparison report
    private pendingChildTab: string;
    private currentColumnsEvent: Subscription;
    private navigationRequestEvent: Subscription;
    private tabNavigationRequestEvent: Subscription;
    private refreshTreeNodesEvent: Subscription;
    private refreshBulkSearchParentNodeEvent: Subscription;
    private searchTypeChangedEvent!: Subscription;
    private componentActivateEvent!: Subscription;

    currentColumnsFromDocList: Array<string> = null;
    isSearchBusy: boolean;
    searchType: string;
    nullViewType = false;
    nullImportHandle = false;
    hideArchivedItems: boolean;
    hideArchivedTooltip: string;
    treeInitialized: boolean;
    customMoreButtonLabel = 'more ...';
    office365Connectors: any[] = [];

    private _currentComponent: NavigationContentComponent;
    public get currentComponent(): NavigationContentComponent {
        return this._currentComponent;
    }
    public set currentComponent(value: NavigationContentComponent) {
        this._currentComponent = value;
    }

    private _selectedNode: GenericTreeNode;
    public get selectedNode(): GenericTreeNode {
        return this._selectedNode;
    }
    public set selectedNode(value: GenericTreeNode) {
        this._selectedNode = value;
    }

    dropdownListComparison: Array<CustomSelectItem> = new Array<CustomSelectItem>(
        new CustomSelectItem({ name: 'Docs in both sets', iconUrl: 'icon_comparison_both_lg.png', iconUrlSelected: 'icon_comparison_both_sm.png' }),
        new CustomSelectItem({ name: 'Docs not in both sets', iconUrl: 'icon_comparison_not_both_lg.png', iconUrlSelected: 'icon_comparison_not_both_sm.png' }),
        new CustomSelectItem({ name: 'Docs in set A only', iconUrl: 'icon_comparison_set_a_lg.png', iconUrlSelected: 'icon_comparison_set_a_sm.png' }),
        new CustomSelectItem({ name: 'Docs in set B only', iconUrl: 'icon_comparison_set_b_lg.png', iconUrlSelected: 'icon_comparison_set_b_sm.png' }),
    );
    selectedDropdownListComparison: CustomSelectItem = this.dropdownListComparison[0];
    comparisonViewA = null;
    comparisonViewB = null;

    constructor(private commonService: CommonService, private restService: RestService, private router: Router,
        private xmlService: XMLService, private treeDataService: TreeDataLoaderService, private metadataService: MetadataService) {
        this.commonService.mainContent = this;

        this.refreshTreeNodesEvent = this.commonService.getMessage(MessageType.refreshLeftTreeNodes).subscribe((data) => {
            if (data?.archivedFlagChanged) {
                this.hideArchivedItems = this.commonService.hideArchivedItems;
                this.hideArchivedItemsSelectionChanged();
            } else {
                this.refreshAllTreeData();
            }
        });

        this.refreshBulkSearchParentNodeEvent = this.commonService.getMessage(MessageType.refreshBulkSearchParentNode).subscribe((data) => {
            this.refreshArchivedNodeDataAfterAccess(data?.archived, data.handle);
        });

        this.currentColumnsEvent = this.commonService.getMessage(MessageType.currentColumns).subscribe((data) => {
            if (data?.length > 0) {
                this.currentColumnsFromDocList = new Array<string>();
                data.forEach(col => {
                    this.currentColumnsFromDocList.push(col);
                })
            } else {
                this.currentColumnsFromDocList = null;
            }
        });

        this.clearPendingNavigationEvent = this.commonService.getMessage(MessageType.clearPendingNavigation).subscribe((dataType) => {
            if (this.pendingNavigation && dataType && this.pendingNavigation.entityType === dataType) {
                this.pendingNavigation = undefined;
            }
        });
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            let menuNode = data?.isDeleteOperation || data?.updateTypeAttributes ? this.getNodeByUpdateStatusType(data.updateStatusType, data.updateTypeAttributes) : this.getNodeByUpdateStatusType(data);
            if (!menuNode) {
                switch (<UpdateStatusType>data) {
                    // project data
                    case UpdateStatusType.WORKING_SET: {
                        this.restService.post(Endpoint.projectManager_listWorkingSets, { projectHandle: this.commonService.currentProject.handle }).then(response => {
                            this.updateProjectData(response);
                        });
                        return;
                    }
                    // searches
                    case UpdateStatusType.SAMPLE_SET: {
                        if (this.navigationTree.treeControl.dataNodes) {
                            menuNode = SearchSearchHistoryMenu;
                        }
                        break;
                    }
                    // review
                    // case UpdateStatusType.REVIEW_BATCH_FOLDER: nodes.push(ReviewFolderMenu); break;
                    // case UpdateStatusType.REVIEW_BATCH: nodes.push(ReviewFolderMenu); break;
                }
            }
            if (data === UpdateStatusType.CORPUS) {
                this.hasAccessToReload = Permissions.checkUserPermission(ObjectActionListType.CORPUS);
                if (this.hasAccessToReload) {
                    this.reloadDataForNodes([menuNode, SearchSearchHistoryMenu], true);
                }
            } else {
                this.reloadDataForNodes([menuNode], true);
            }
            if (data === DrObjectType.CONNECTOR) {
                this.getOffice365Connectors();
            }
        });

        this.navigationRequestEvent = this.commonService.getMessage(MessageType.navigationRequest).subscribe((data) => {
            this.navigationRequestHandler(data);
        });

        this.tabNavigationRequestEvent = this.commonService.getMessage(MessageType.tabNavigationRequest).subscribe((data) => {
            this.tabNavigationRequestHandler(data);
        });

        (<any>window).navigateTo = (data: any) => {
            this.navigationRequestHandler(data);  // from an external window
        };
    }

    isChildNode(node: GenericTreeNode): boolean {
        return node?.isSelectable && node.level > 0 && node.name !== SearchSearchHistoryMenu.name
            && node.name !== AnalyticComparisonMenu.name && node.name !== AnalyticProfileMenu.name;
    }

    refreshAllTreeData(): void {
        this.treeDataService.loadData(true).then((loadDataResponse) => {
            if (loadDataResponse && this.selectedNode) {
                if (this.selectedNode.data instanceof WorkflowStep || this.selectedNode.data instanceof ExportVolume || this.selectedNode.data instanceof ExportComparisonReport) {
                    this.pendingNavigation = this.selectedNode.data;
                    this.navigationRequestHandler(this.selectedNode.parent?.data);
                } else {
                    this.navigationRequestHandler(this.selectedNode.data);
                }
            }
            this.hideArchivedItemsSelectionChanged(this.selectedNode.entityType);
        });
    }

    reloadDataForNodes(nodes: Array<GenericTreeNode>, refresh?: boolean) {
        if (nodes.length > 0) {
            nodes.forEach(node => {
                this.navigationTree.loadNodeData(node, refresh);
            });
        }
    }

    getNodeByUpdateStatusType(type: UpdateStatusType, updateTypeAttributes?: any): GenericTreeNode {
        switch (type) {
            // imports
            case UpdateStatusType.CORPUS: return ImportMenu;
            // exports
            case UpdateStatusType.EXPORT_VIEW: return ExportMenu;
            // project data
            case UpdateStatusType.CUSTODIAN: return ProjectDataCustodianMenu;
            case UpdateStatusType.MEDIAID: return ProjectDataMediaIDsMenu;
            case UpdateStatusType.BATCH: return ProjectDataBatchesMenu;
            case UpdateStatusType.TAG_VIEW: return ProjectDataTagMenu;
            case UpdateStatusType.DYNAMIC_FOLDER_VIEW: return ProjectDataFolderMenu;
            // searches
            case UpdateStatusType.SAVED_SEARCH: return SearchSavedSearchMenu;
            case UpdateStatusType.BULKSEARCH_IN_SEARCH_HISTORY: {
                let targetBulkSearch: GenericTreeNode;
                _.find(this.navigationTree.treeControl.dataNodes, (node) => {
                    if (node?.data?.handle === this.pendingNavigation.parent.data.handle) {
                        targetBulkSearch = node;
                    }
                });
                return targetBulkSearch;
            }
            // workflows
            case UpdateStatusType.WORK_FLOW: return WorkflowMenu;
            // analytic
            case UpdateStatusType.COMPARISON: return AnalyticComparisonMenu;
            case UpdateStatusType.PROFILE: return AnalyticProfileMenu;
            case UpdateStatusType.SEARCH: {
                let node: GenericTreeNode = null;
                const currentUserName = this.commonService.currentUser.name;
                if (updateTypeAttributes) {
                    _.each(updateTypeAttributes.SEARCH, function (value) {
                        const userName = value.find(val => val.name === 'username');
                        if (userName.value === currentUserName) {
                            node = SearchSearchHistoryMenu;
                        }
                    });
                } else {
                    node = SearchSearchHistoryMenu;
                }
                return node;
            }
            default: return null;
        }
    }

    getOffice365Connectors(): void {
        if (Permissions.checkUserPermission(ObjectActionListType.CONNECTOR)) {
            const requestInfo = {
                endpoint: Endpoint.adminOrgManager_listConnectors,
                responseName: 'connectors',
                data: {
                    organizationName: this.commonService?.currentUser?.customerName
                }
            };
            this.restService.post(requestInfo.endpoint, requestInfo.data).then((response) => {
                if (response) {
                    if (isArray(response[requestInfo.responseName])) {
                        this.office365Connectors = (response[requestInfo.responseName].filter(c => (c.tenantDomain?.length > 0) && (c.type === Type.Exchange || c.type === Type.Sharepoint))) as Array<any>;
                    }
                }
            });
        }
    }

    ngOnInit(): void {
        // permissions
        this.hasWorkBasketAccess = Permissions.checkUserPermission(ObjectActionListType.WORK_BASKET);
        this.hasComparisionPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] }]);
        WorkflowMenu.showContextMenuButton = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.CREATE] }]);
        this.nullImportHandle = false;

        // show/hide context menu on Analytics node based on permissions
        AnalyticMenu.showContextMenuButton = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] },
        { permissionType: ObjectActionListType.PROFILES, permissionActions: [ActionType.CREATE] }], true);

        if (this.commonService.currentProject) {
            this.setInitTreeData();
        }

        this.searchTypeChangedEvent = this.searchComponent.searchTypeChangedEvent.subscribe((info: { searchType: SearchType, offset?: number, resetSearchSize?: boolean }) => {
            if (info) {
                if (info.searchType === SearchType.BULK || info.searchType === SearchType.METADATA_BULK) {
                    this.searchBarResizer.collapsed = false;
                    this.searchBarResizer.collapsibleToMinSize = true;
                    this.searchBarResizer.expandableToMax = false;
                    this.searchBarResizer.minSize = 80;
                    this.searchBarResizer.initSize = 330;
                    this.searchBarResizer.setElementSize(330);
                } else if (info.searchType === SearchType.FREEFORM) {
                    if (info.resetSearchSize) {
                        this.simpleSearchSize = 120;
                    }
                    if (isNumber(info.offset)) {
                        this.simpleSearchSize += info.offset;
                    }
                    this.searchBarResizer.collapsibleToMinSize = true;
                    this.searchBarResizer.expandableToMax = true;
                    this.searchBarResizer.collapsed = true;
                    this.searchBarResizer.minSize = 120;
                    this.searchBarResizer.initSize = 120;
                    this.simpleSearchSize = Math.min(window.innerHeight * .8, // No taller than 80% of the window
                        Math.max(this.simpleSearchSize, this.searchBarResizer.minSize));
                    this.searchBarResizer.setElementSize(this.simpleSearchSize);
                } else {
                    this.searchBarResizer.minSize = 120;
                    this.searchBarResizer.initSize = 120;
                    this.searchBarResizer.setElementSize(120);
                }
                this.searchType = info.searchType;
            }
        });
        this.getOffice365Connectors();
        this.metadataService.getCustomMetadataFields(SettingsType.project, this.commonService?.currentProject?.handle, null);
    }

    isBulkSearchParentNode(nodeToCheck: GenericTreeNode): boolean {
        return (nodeToCheck?.entityType === EntityType.searchResult && (nodeToCheck.data?.task === SearchType.BULK || nodeToCheck.data?.task === SearchType.METADATA_BULK));
    }

    setInitTreeData(): void {
        this.restService.post(Endpoint.projectManager_listWorkingSets, { projectHandle: this.commonService.currentProject.handle }, false).then(projectDataResponse => {
            if (projectDataResponse) {
                this.updateProjectData(projectDataResponse);
            }

            this.restService.post(Endpoint.corpusSetManager_getCorpusSetByName, { projectHandle: this.commonService.currentProject?.handle, corpusSetName: 'AllCorpora' }).then((corpusSetResponse) => {
                if (corpusSetResponse?.corpusSet?.handle) {
                    this.commonService.importsHandle = corpusSetResponse.corpusSet.handle;
                    ImportMenu.requestInfo.data = { corpusSetHandle: this.commonService.importsHandle, startIndex: 0, count: 50 };
                    ImportMenu.data = { handle: this.commonService.importsHandle, name: ImportMenu.name };
                    ImportMenu.isSortedAlphabetically = true;
                    const projectHandle = this.commonService.currentProject?.handle;
                    ProjectDataCustodianMenu.requestInfo.data.projectHandle = projectHandle;
                    ProjectDataMediaIDsMenu.requestInfo.data.projectHandle = projectHandle;
                    ProjectDataBatchesMenu.requestInfo.data.projectHandle = projectHandle;
                    const projDataHandle = this.commonService.projectData.handle;
                    ProjectDataTagMenu.requestInfo.data.workingSetHandle = projDataHandle;
                    const exportToRelativityContextMenu = ProjectDataMenu.contextMenuOptions.find(o => o.name === 'Export to Review ...');
                    if (exportToRelativityContextMenu) {
                        const isRelativityArtifactId: boolean = this.commonService.currentProject?.reviewProjectId ? true : false;
                        exportToRelativityContextMenu.visible = exportToRelativityContextMenu.isVisible && this.commonService.currentProject?.isTlsExpress && isRelativityArtifactId;
                    }
                    ProjectDataFolderMenu.requestInfo.data.projectHandle = projectHandle;
                    ExportMenu.requestInfo.data = { handle: projDataHandle, startIndex: 0, count: 50 };
                    ExportMenu.isSortedAlphabetically = true;
                    SearchSavedSearchMenu.requestInfo.data.projectHandle = projectHandle;
                    SearchSearchHistoryMenu.requestInfo.data.projectHandle = projectHandle;
                    AnalyticComparisonMenu.requestInfo.data.projectHandle = projectHandle;
                    AnalyticProfileMenu.requestInfo.data.handle = projectHandle;
                    WorkflowMenu.requestInfo.data.projectHandle = projectHandle;

                    this.searchComponent.target.handle = projDataHandle;
                    this.initializeNavTree().then(treeInitialized => {
                        if (treeInitialized) {
                            this.treeInitialized = true;
                            const dataToSelect = this.commonService.selectedNavigationNode?.data;
                            if (dataToSelect) {
                                if (dataToSelect instanceof WorkflowStep || dataToSelect instanceof ExportVolume || dataToSelect instanceof ExportComparisonReport) {
                                    this.pendingNavigation = dataToSelect;
                                    this.pendingNavigationTab = this.commonService.docListTabBeforeViewer;
                                    this.navigationRequestHandler(this.commonService.selectedNavigationNode.parent?.data);
                                } else if (dataToSelect.isBulkStep) {
                                    this.pendingNavigation = dataToSelect;
                                    this.loadDataForEntityTypeAndSelectNode(this.commonService.selectedNavigationNode.parent);
                                } else {
                                    this.navigationRequestHandler(dataToSelect, this.commonService.selectedNavigationNode.navigationInfo);
                                }
                            } else {
                                this.dashboardTree.selectNode(DashboardMenu);
                            }
                            this.navigationTree.treeDataUpdated();
                            const exportsNode = this.navigationTree.getNodeBy(n => n.entityType === EntityType.export);
                            if (exportsNode && this.commonService.currentProject?.tlsDefaultExportStreamHandle) {
                                const exportStreams = exportsNode.children;
                                const defaultExportStream = exportStreams ? exportStreams.find(es => es.data?.handle === this.commonService.currentProject?.tlsDefaultExportStreamHandle) : null;
                                if (defaultExportStream?.data) {
                                    this.commonService.currentProject.tlsDefaultExportStream = defaultExportStream.data;
                                }
                            }
                        } else {
                            if (this.nullImportHandle) {
                                this.commonService.openErrorModal('System encountered an error during project creation and this project cannot be used. Please contact your System Administrator.');
                            } else if (!this.commonService.projectDataHandle) {
                                this.commonService.openErrorModal('Unable to get project data while initializing navigation tree!');
                            }
                        }
                    });
                }
                else {
                    this.commonService.importsHandle = null;
                    this.nullImportHandle = true;
                }
            });
        }, (error) => {
            if (error) {
                this.commonService.openErrorModal('Unable to get project data while initializing navigation tree!');
            }
        });
    }

    initializeNavTree(): Promise<any> {
        this.treeDataService.createNavTreeNodes();
        this.treeDataService.resetSearchCount();
        this.navigationTree.setData(this.treeDataService.nodes);
        this.hideArchivedTooltip = `Archived items are views that have not been accessed for ${this.commonService.lastAccessTimeForArchival} days`;
        this.dashboardTree.setData([DashboardMenu]);
        const filterBy = (treeNode: GenericTreeNode) => (!treeNode.permissions || (treeNode.permissions && Permissions.checkUserPermissions(treeNode.permissions))) && (this.hideArchivedItems ? !treeNode.isArchivedNode() : true);
        this.navigationTree.treeControl.dataNodes.forEach(nodeItem => {
            nodeItem.filterBy = filterBy;
        });
        return this.treeDataService.loadData(true);
    }

    updateProjectData(response) {
        if (response && response.workingSets) {
            response.workingSets.forEach(element => {
                if (element.name === 'CaseData' || element.name === 'Project Data') {
                    ProjectDataMenu.data = this.commonService.projectData = new WorkingSet(element);
                    ProjectDataMenu.customNumber = this.commonService.projectData.workingSetView ? this.commonService.projectData.workingSetView.documentCount : 0;
                } else if (element.name === 'discardpile' || element.name === 'Discard Pile') {
                    DiscardPileMenu.data = this.commonService.discardPile = new WorkingSet(element);
                    DiscardPileMenu.customNumber = this.commonService.discardPile.workingSetView ? this.commonService.discardPile.workingSetView.documentCount : 0;
                }
            });
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        this.nullViewType = false;
        if (node) {
            if (node !== DashboardMenu && this.dashboardTree.selectedNode) {
                this.dashboardTree.selectedNode = null;
            }
            if (node === DashboardMenu && this.navigationTree.selectedNode) {
                this.navigationTree.selectedNode = null;
            }
            if (node?.isButton && node.parent?.requestInfo?.data && (node.parent.entityType === EntityType.searchResult || node.parent.entityType === EntityType.import || node.parent.entityType === EntityType.export)) {
                const resultsCount = node.parent.entityType === EntityType.searchResult ? node.parent.requestInfo.data.count + 10 : 0;
                const returnedPromise = this.treeDataService.loadMoreResults(resultsCount, node.parent, true);
                if (returnedPromise) {
                    returnedPromise.then(() => {
                        this.hideArchivedItemsSelectionChanged(node.entityType);
                    })
                }
            }
            if (node?.isButton && (node.entityType === EntityType.export || node.entityType === EntityType.import)) {
                // update the more button's label to a loading icon and text when the more button in export streams or volumes is clicked
                node.name = 'Loading ...';
                node.isDisabled = true;
                node.isLoading = true;
            }
            if (node.data && node.entityType === EntityType.searchResult && isNullOrUndefined(node.data.viewType)) {
                this.nullViewType = true;
                this.breadcrumbComponent.selectedTreeItemChanged(node);
                this.searchComponent.queryString = '';
                this.searchComponent.selectedTreeItemChanged(node);
            } else {
                let skipNavigation = false;
                let selectedTab = null;
                if (this.selectedNode && this.selectedNode.data && this.selectedNode.data.handle
                    && node.data && node.data.handle === this.selectedNode.data.handle) {
                    skipNavigation = true;
                    selectedTab = this.selectedNode.navigationInfo ? this.selectedNode.navigationInfo.selectedTab : null;
                }
                const isFromDocViewer = this.commonService.selectedNavigationNode &&
                    (!this.selectedNode || this.pendingNavigationTab);

                if (node.isButton) {
                    skipNavigation = true;
                    this.selectedNode.isManualSelection = true;
                    let selectedOldNode = _.cloneDeep(this.selectedNode);
                    node.prevOldNode = selectedOldNode;
                } else {
                    this.selectedNode = node;
                }

                this.commonService.selectedNavigationNode = _.cloneDeep(this.selectedNode);
                if (!isFromDocViewer) {
                    this.commonService.currentClusterView = null;
                }
                if (!skipNavigation) {
                    if (node.navigationInfo && node.navigationInfo.tabs.length > 0) {
                        if (node.navigationInfo.selectedTab && isFromDocViewer) {
                            this.pendingNavigationTab = null;
                            this.commonService.docListTabBeforeViewer = null;
                            this.navigateTo(node.navigationInfo.selectedTab);
                        } else {
                            this.navigateTo(node.navigationInfo.tabs.filter(t => t.default)[0]);
                        }

                        if (this.commonService.matrixGridsSelection?.isDocViewerOpened && !this.commonService.userSelectionClick &&
                            (this.commonService.docListTabBeforeViewer === 'communication-matrix' || this.commonService.docListTabBeforeViewer === 'domain-matrix')) {
                            node.navigationInfo.selectedTab = node?.navigationInfo?.tabs?.filter(t => t?.path === this.commonService?.docListTabBeforeViewer)[0];
                            if (node.navigationInfo?.tabs?.length === 1 && node.navigationInfo?.tabs[0] && node.navigationInfo?.tabs[0]?.path === 'summary') {
                                this.commonService.matrixGridsSelection.isDocViewerOpened = false;
                            }
                            this.navigateTo(node.navigationInfo?.selectedTab);
                        } else if (this.commonService.matrixGridsSelection && (node.navigationInfo?.selectedTab?.path === 'document-list' || this.commonService.userSelectionClick) && this.commonService.matrixGridsSelection?.isDocViewerOpened) {
                            this.commonService.matrixGridsSelection.isDocViewerOpened = false;
                        }
                    }
                    this.breadcrumbComponent.selectedTreeItemChanged(node);
                    this.searchComponent.selectedTreeItemChanged(node);
                } else if (selectedTab) {
                    this.selectedNode.navigationInfo.selectedTab = selectedTab;
                    this.commonService.selectedNavigationNode.navigationInfo.selectedTab = selectedTab;
                    if (selectedTab?.path === 'document-list' && this.commonService.matrixGridsSelection?.isDocViewerOpened) {
                        this.commonService.matrixGridsSelection.isDocViewerOpened = false;
                    }
                }
                if (this.selectedNode && this.selectedNode.data && this.selectedNode.data instanceof Comparison) {
                    if (!skipNavigation) {
                        if (isFromDocViewer) {
                            this.selectedDropdownListComparison = this.getSelectedDropdownListComparison(isFromDocViewer);
                            this.selectedComparisonTypeChanged(this.selectedDropdownListComparison, true, true);
                        } else {
                            this.selectedDropdownListComparison = this.dropdownListComparison[0];
                        }
                    }
                    this.commonService.selectedComparisonNavigation = _.cloneDeep(this.selectedNode.data);
                    this.setComparisonData(node.data.iconColorDocumentSetA, node.data.iconColorDocumentSetB);
                } else {
                    this.commonService.selectedComparisonNavigation = null;
                }
            }
        }
    }

    getSelectedDropdownListComparison(isFromDocViewer = false) {
        const currentNode = (isFromDocViewer && this.commonService.selectedComparisonNavigation) ? this.commonService.selectedComparisonNavigation : this.selectedNode.data;
        let comparisonEntry = this.dropdownListComparison[0];
        if (currentNode.docListHandle) {
            if (currentNode.viewAintersectionB && currentNode.viewAintersectionB.handle === currentNode.docListHandle) {
                comparisonEntry = this.dropdownListComparison[0];
            }
            if (currentNode.viewAdisjunctionB && currentNode.viewAdisjunctionB.handle === currentNode.docListHandle) {
                comparisonEntry = this.dropdownListComparison[1];
            }
            if (currentNode.viewAcomplimentB && currentNode.viewAcomplimentB.handle === currentNode.docListHandle) {
                comparisonEntry = this.dropdownListComparison[2];
            }
            if (currentNode.viewBcomplimentA && currentNode.viewBcomplimentA.handle === currentNode.docListHandle) {
                comparisonEntry = this.dropdownListComparison[3];
            }
            if (isFromDocViewer && comparisonEntry) {
                comparisonEntry.data = currentNode.docListHandle;
            }
        }
        return comparisonEntry
    }

    setComparisonData(colorA = '', colorB = ''): void {
        if (this.selectedNode && this.selectedNode.data) {
            // set doc list handles
            this.dropdownListComparison[0].data = this.selectedNode.data.viewAintersectionB ? this.selectedNode.data.viewAintersectionB.handle : null;
            this.dropdownListComparison[1].data = this.selectedNode.data.viewAdisjunctionB ? this.selectedNode.data.viewAdisjunctionB.handle : null;
            this.dropdownListComparison[2].data = this.selectedNode.data.viewAcomplimentB ? this.selectedNode.data.viewAcomplimentB.handle : null;
            this.dropdownListComparison[3].data = this.selectedNode.data.viewBcomplimentA ? this.selectedNode.data.viewBcomplimentA.handle : null;

            // search view in tree, if not found create a default one
            this.comparisonViewA = { name: this.selectedNode.data.viewA.name };
            Object.assign(this.comparisonViewA, this.getIconByViewType(this.selectedNode.data.viewA.viewType));
            if (colorA.indexOf('#') === -1) {
                this.comparisonViewA.iconColor = colorA;
            } else {
                this.comparisonViewA.iconHexColor = colorA;
            }
            this.comparisonViewB = { name: this.selectedNode.data.viewB.name };
            Object.assign(this.comparisonViewB, this.getIconByViewType(this.selectedNode.data.viewB.viewType));
            if (colorB.indexOf('#') === -1) {
                this.comparisonViewB.iconColor = colorB;
            } else {
                this.comparisonViewB.iconHexColor = colorB;
            }
        }
    }

    getIconByViewType(viewType: ViewType): any {
        switch (viewType) {
            case ViewType.UNASSIGNED_CUSTODIAN:
            case ViewType.CUSTODIAN:
                return { icon: 'fa-user-circle' };
            case ViewType.MEDIAID:
            case ViewType.UNASSIGNED_MEDIAID:
                return { icon: 'fa-laptop' };
            case ViewType.BATCH:
            case ViewType.UNASSIGNED_BATCH:
                return { icon: 'fa-archive' };
            case ViewType.TAG:
                return { icon: 'fa-tag' };
            case ViewType.WORKING_SET_RESULT:
            case ViewType.CORPUS_RESULT:
            case ViewType.CORPUS_SET_RESULT:
                return { icon: 'fa-search' };
            case ViewType.GROUP:
            case ViewType.DYNAMIC_FOLDER:
                return { icon: 'fa-folder' };
        }
        return { icon: '' };
    }

    toggleWorkbasket(): void {
        this.resizers.last.toggleCollapse();
    }

    selectTab = (tab: NavigationTab) => this.preselectNodeCallback(() => { this.navigateTo(tab); });

    navigateTo(tab: NavigationTab): void {
        if (tab && !isNullOrUndefined(this.selectedNode)) {
            this.selectedNode.navigationInfo.selectedTab = tab;
            if (this.commonService.selectedNavigationNode?.navigationInfo) {
                this.commonService.selectedNavigationNode.navigationInfo.selectedTab = tab;
            }
            if (tab.path) {
                if (this.router.url.includes(`(project-focus:${tab.path})`) && this.currentComponent && this.currentComponent.selectedTreeItemChanged) {
                    //!this.router.url.includes(`(project-focus:${tab.path})`) need to check if the same selected tab is clicked again
                    if (this.currentComponent && (this.currentComponent as any)?.matrixComponent && !this.router.url.includes(`(project-focus:${tab.path})`)) {
                        this.currentComponent.selectedTreeItemChanged(this.selectedNode);
                    } else if (this.currentComponent && !(this.currentComponent as any)?.matrixComponent) {
                        this.currentComponent.selectedTreeItemChanged(this.selectedNode);
                    }
                } else {
                    this.router.navigate(['/main-content', { outlets: { 'project-focus': [tab.path] }, skipLocationChange: true }], { skipLocationChange: true });
                }
            }
            if (this.selectedNode.isArchivedNode()) {
                // delay tree child node update, until child doc list is rendered
                setTimeout(() => {
                    this.updateDataForArchivedNode(tab.path);
                }, 300);
            }
        }
    }

    updateDataForArchivedNode(path: string, nodeToUpdate = null): void {
        if (path && (this.selectedNode?.data || nodeToUpdate?.data)) {
            const nodeData = nodeToUpdate?.data || this.selectedNode?.data;
            let viewHandle = nodeData.view ? nodeData.view.handle : nodeData.handle;
            switch (path) {
                case 'document-list': {
                    if (this.selectedNode.entityType === EntityType.comparison && nodeData.viewAintersectionB) {
                        viewHandle = nodeData.viewAintersectionB.handle;
                    }
                    this.restService.post(Endpoint.viewManager_getView, { handle: viewHandle, selectedAttributes: [] }).then((response) => {
                        if (response?.view && nodeData) {
                            this.refreshArchivedNodeDataAfterAccess(response.view.archived, nodeData.handle);
                        }
                    });
                    break;
                }
                case 'profile-detail': {
                    this.restService.post(Endpoint.profileManager_getProfile, {
                        handle: nodeData.handle
                    }).then((response) => {
                        if (response?.profile) {
                            this.refreshArchivedNodeDataAfterAccess(response.profile.archived, nodeData.handle);
                        }
                    })
                    break;
                }
                case 'workflow': {
                    this.restService.post(Endpoint.workFlowManager_getWorkFlow, { handle: viewHandle }).then((response) => {
                        if (response?.workFlow) {
                            this.refreshArchivedNodeDataAfterAccess(response.workFlow.archived, nodeData.handle);
                        }
                    })
                    break;
                }
            }
        }
    }

    refreshArchivedNodeDataAfterAccess(archivedFlag: boolean, selectedNodeHandle: string): void {
        const nodeToUpdate = this.navigationTree.getNodeByHandle(selectedNodeHandle);
        if (nodeToUpdate) {
            if (nodeToUpdate.data.view) {
                nodeToUpdate.data.view.archived = archivedFlag;
            } else {
                nodeToUpdate.data.archived = archivedFlag;
            }
            this.navigationTree.treeDataUpdated();
        }

    }

    preselectNodeCallback = (nodeOrAction: GenericTreeNode | any) => {
        if (nodeOrAction) {
            if (this.currentComponent && (this.currentComponent.saveChanges || this.currentComponent.saveChangesWithPromise)
                && this.currentComponent.isPageDirty && this.currentComponent.isPageDirty()) {
                this.commonService.openConfirmationModal('There are unsaved changes.',
                    'Unsaved Changes', 'Save Changes', 'Cancel', 'Discard Changes', 'unsaved-changes-message').then(result => {
                        if (result.modalResult) {
                            if (result.modalData === 'Save Changes') {
                                if (this.currentComponent.saveChangesWithPromise) {
                                    this.currentComponent.saveChangesWithPromise().then(saveResult => {
                                        if (saveResult) {
                                            if (nodeOrAction instanceof GenericTreeNode) {
                                                this.navigationTree.selectNode(nodeOrAction);
                                            } else {
                                                nodeOrAction();
                                            }
                                        }
                                    });
                                } else {
                                    this.currentComponent.saveChanges();
                                    if (nodeOrAction instanceof GenericTreeNode) {
                                        this.navigationTree.selectNode(nodeOrAction);
                                    } else {
                                        nodeOrAction();
                                    }
                                }
                            } else {
                                if (nodeOrAction instanceof GenericTreeNode) {
                                    this.navigationTree.selectNode(nodeOrAction);
                                } else {
                                    nodeOrAction();
                                }
                            }
                        }
                    });
            } else {
                if (nodeOrAction instanceof GenericTreeNode) {
                    this.navigationTree.selectNode(nodeOrAction);
                } else {
                    nodeOrAction();
                }
            }
        }
    }


    onRouterOutletActivate(component: NavigationContentComponent): void {
        if (component) {
            this.currentComponent = component;
            if (component.selectedTreeItemChanged) {
                component.selectedTreeItemChanged(this.selectedNode);
            }
            if (component.selectTreeNode) {
                this.componentActivateEvent = component.selectTreeNode.subscribe((data: any) => {
                    if (isString(data)) {
                        // Select the node from the expanded imports/exports
                        if (this.selectedNode?.isButton && this.selectedNode.isLoading && (this.selectedNode.entityType === EntityType.import || this.selectedNode.entityType === EntityType.export)) {
                            this.selectedNode = this.selectedNode.parent;
                        }
                        const node = this.navigationTree.getNodeByHandle(data, this.selectedNode);
                        // if the node is not present and is hidden by the 'more ... ' button in imports/exports - load the remaining results and select the node
                        const hasLoadRemainingButton = this.selectedNode.children.find(node => node.isButton);
                        if (!node && this.selectedNode && (this.selectedNode.entityType === EntityType.import || this.selectedNode.entityType === EntityType.export) && hasLoadRemainingButton) {
                            const parentNode = this.selectedNode;
                            this.selectedNodeChangedCallback(this.selectedNode.children[this.selectedNode.children.length - 1]);
                            const findAndSelectNode = setInterval(() => {
                                // check if the remaining children are loaded.
                                const node = this.navigationTree.getNodeByHandle(data, parentNode);

                                if (node) {
                                    this.navigationTree.selectNode(node);
                                    clearInterval(findAndSelectNode);
                                }
                            }, 5000);
                        }

                        if (node) {
                            this.navigationTree.selectNode(node);
                        }
                    } else {
                        if (data && data.handle) {
                            const node = this.navigationTree.getNodeByHandle(data.handle, this.selectedNode);
                            if (node) {
                                this.navigationTree.selectNode(node);
                            } else if (data.searchToSelect) {
                                // search node is not in tree, added it at top of the searches and then navigate to it ECASL-28649
                                const nodeToSelect = this.createSearchNode(data.searchToSelect);
                                this.navigationTree.selectNode(nodeToSelect);
                            }
                        }
                    }
                });
            }
        }
    }

    breadcrumbClicked(breadcrumb: GenericTreeNode): void {
        if (breadcrumb) {
            if (breadcrumb.data && breadcrumb.data.handle) {
                const node = this.navigationTree.getNodeByHandle(breadcrumb.data.handle, breadcrumb.parent);
                if (node) {
                    this.preselectNodeCallback(node);
                }
            } else {
                this.preselectNodeCallback(breadcrumb);
            }
        }
    }

    private createSearchNode(data: SearchResult): GenericTreeNode {
        const targetNode = SearchSearchHistoryMenu.createChild(data);
        targetNode.parent = SearchSearchHistoryMenu;
        SearchSearchHistoryMenu.children.unshift(targetNode);
        this.navigationTree.treeDataUpdated();
        return targetNode;
    }

    navigationRequestHandler(target: any, navigationInfo: NavigationInfo = null): void {
        this.preselectNodeCallback(() => {
            let loadPending = false;
            this.pendingChildNavigation = undefined;
            this.pendingChildTab = 'document-list';
            if (isArray(target)) {
                this.pendingChildNavigation = target[1];
                if (target.length > 2) {
                    this.pendingChildTab = target[2];
                }
                target = target[0];
            } else if (target instanceof GenericTreeNode && target.data instanceof WorkflowStep) {
                this.pendingChildNavigation = target.data; // workflow step
                target = target.parent.data;    // parent workflow
            }
            if (target) {
                const handle = target.activeRule ? target.activeRule.handle : target instanceof GenericTreeNode ? target.data?.handle : target.handle;
                const node = this.navigationTree.getNodeByHandle(handle, this.isChildNode(target) ? this.getParentNodeByEntityType(target.entityType) : null);
                if (node && navigationInfo) {
                    node.navigationInfo = navigationInfo;
                }
                if (node && isUndefined(this.pendingChildNavigation)) {
                    this.navigationTree.selectNode(node);
                    loadPending = true;
                } else if (!target.handle && target.entityType === EntityType.workFlow && isUndefined(this.pendingChildNavigation)) {
                    this.navigationTree.selectNode(this.navigationTree.selectedNode.parent);
                    loadPending = true;
                } else if (target.handle &&
                    (target.entityType === EntityType.searchResult || target.entityType === UpdateStatusType.RESULT_VIEW)) {
                    this.pendingNavigation = target;
                    this.reloadDataForNodes([SearchSearchHistoryMenu], true);
                    loadPending = true;
                } else if (target.handle && (target.entityType === EntityType.workFlow || this.pendingChildNavigation instanceof WorkflowStep)) {
                    this.navigationTree.treeControl.expand(WorkflowMenu);
                    if (this.pendingChildNavigation) {
                        this.navigationTree.selectNode(node);
                        loadPending = true;
                        this.pendingNavigation = this.pendingChildNavigation;
                        this.pendingChildNavigation = undefined;
                    } else {
                        this.pendingNavigation = target;
                    }
                } else {
                    if (target.parent) {
                        this.navigationTree.treeControl.expand(target.parent);
                        loadPending = true;
                    }
                    this.pendingNavigation = target instanceof GenericTreeNode ? target.data : target;
                }
                if (!loadPending) {
                    if (this.pendingNavigation instanceof SearchResult) {
                        this.reloadDataForNodes([SearchSearchHistoryMenu], true);
                    } else {
                        if (!this.pendingNavigation) {
                            console.error('Not navigating to requested node');
                            console.error(target);
                        }
                    }
                }
            }
        });
    }

    tabNavigationRequestHandler(tab: any): void {
        this.navigateTo(tab);
    }

    finishLoadingCallback = (node: GenericTreeNode): void => {
        if (this.pendingNavigation && node) {
            const navigationHandle = isString(this.pendingNavigation) ? this.pendingNavigation : this.pendingNavigation.handle; //when search is triggered from external doc viewer pendingNavigation is a handle
            let targetNode = this.pendingNavigation ? this.navigationTree.getNodeByHandle(navigationHandle, node) : undefined;
            if (targetNode && this.pendingNavigationTab) {
                targetNode.navigationInfo.selectedTab = targetNode.navigationInfo.tabs.filter(t => t.path === this.pendingNavigationTab)[0];
            }
            if (!targetNode) {
                if (this.pendingNavigation.type === EntityType.tagView) {
                    targetNode = this.navigationTree.getNodeByAttr('tagHandle', this.pendingNavigation.handle, ProjectDataTagMenu);
                } else if (this.pendingNavigation.name && this.pendingNavigation.entityType && !this.pendingNavigation?.data?.isBulkStep) {
                    // Use these clues to find the newly created object that will now have complete details loaded into the tree
                    const startNode = this.getNodeByUpdateStatusType(this.pendingNavigation.entityType);
                    if (this.pendingNavigation.data instanceof WorkflowStep) {
                        targetNode = this.navigationTree.getNodeByField('handle', this.pendingNavigation.data.handle, startNode);
                    } else {
                        targetNode = this.navigationTree.getNodeByField('name', this.pendingNavigation.name, startNode);
                    }
                }
                else if (this.pendingNavigation?.data?.isBulkStep) {
                    // need a setTimeout here as all bulk search children may not be available by this time
                    // BULKSEACH_IN_SEARCH_HISTORY
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.BULKSEARCH_IN_SEARCH_HISTORY);
                    targetNode = this.navigationTree.getNodeByHandle(this.pendingNavigation.data.handle, node);

                    if (!targetNode) {
                        this.getTasks().then((response) => {
                            if (this.pendingNavigation) {
                                this.pendingNavigation.data.compareExports = response.tasks[0]?.compareExportList;
                                targetNode = this.createSearchNode(this.pendingNavigation.data);
                                this.navigationTree.selectNode(targetNode);
                                this.selectedNode.setChildren(response.tasks[0].compareExportList);
                                this.pendingNavigation = undefined;
                            }
                        });
                    }
                }
            }
            if (targetNode) {
                this.navigationTree.selectNode(targetNode);
                this.pendingNavigation = undefined;
            } else if (this.pendingNavigation.entityType === UpdateStatusType.RESULT_VIEW) { // Coming from workbasket
                this.navigationTree.treeControl.expand(SearchSearchHistoryMenu);
                const navCopy = this.pendingNavigation;
                navCopy.name = navCopy.description;
                this.pendingNavigation = undefined;
                setTimeout(() => {
                    this.navigationTree.selectNode(this.createSearchNode(navCopy));
                });
            } else {
                // After loading the node, the result is still not there.  Try again shortly
                setTimeout(() => {
                    if (this.pendingNavigation instanceof SearchResult || this.pendingNavigation?.data instanceof SearchResult) {
                        this.pendingNavigation = this.pendingNavigation?.data instanceof SearchResult ? this.pendingNavigation.data : this.pendingNavigation;
                        this.getTasks().then((response) => {
                            const compareExports = response?.tasks[0]?.compareExports ? response.tasks[0].compareExports : response.tasks[0]?.compareExportList;
                            this.pendingNavigation?.compareExports ? this.pendingNavigation.compareExports = compareExports : this.pendingNavigation.compareExportList = compareExports;
                            const newNode = this.createSearchNode(this.pendingNavigation);
                            this.navigationTree.selectNode(newNode);
                            this.selectedNode.setChildren(compareExports);
                            this.selectedNode.preloadedData = null;
                            this.navigationTree.treeDataUpdated();
                            this.pendingNavigation = undefined;
                        });
                    }
                    else if (this.pendingNavigation?.entityType === 'CORPUS' || this.pendingNavigation?.entityType === 'EXPORT_VIEW') {
                        if (node && node.children?.length > 0) {
                            const hasHiddenChildren = node.children.find(childNode => childNode.isButton);
                            if (hasHiddenChildren) {
                                return;
                            }
                        }
                    } else if (this.pendingNavigation) {
                        this.navigationTree.loadNodeData(node, true);
                    }
                }, 750);
            }
            if (this.pendingChildNavigation && this.pendingChildNavigation.handle) {
                setTimeout(() => {
                    // By selecting the parent first (only momentarily) it loads the children nodes.  If we attempted to select the child
                    // directly instead, the children of that parent would not be loaded into the tree and would not be found
                    const foundChild = this.selectedNode.children.findIndex(obj => {
                        if (obj?.data?.handle && this.pendingChildNavigation?.handle) {
                            return obj.data.handle === this.pendingChildNavigation.handle;
                        }
                    });
                    if (foundChild !== -1) {
                        const tab = this.selectedNode.children[foundChild].navigationInfo.tabs.filter(t => t.path === this.pendingChildTab)[0];
                        this.navigationTree.selectNode(this.selectedNode.children[foundChild]);
                        this.navigationTree.treeDataUpdated();
                        this.navigateTo(tab);
                        this.pendingChildNavigation = undefined;
                    }
                }, 2000);
            }
        }
        if (node && node.entityType === EntityType.workFlow && node.children.length > 0) {
            node.children.forEach(nodeChild => {
                if (nodeChild.data instanceof WorkflowStep) {
                    const stepType = _.find(nodeChild.data.attributes, attr => attr.name === 'steptype');
                    const name = stepType ? stepType.value : 'Term Query';
                    nodeChild.name = name + ': ' + (this.xmlService.xmlToQueryConverter(nodeChild.data.query));
                }
            });
        }
        if ((node?.entityType === EntityType.export || this.isBulkSearchParentNode(node)) && node.children.length > 0) {
            node.children.forEach(expVol => expVol.filterBy = node.filterBy);
        }
        // will update the more button name as per current state of the hide archieved flag
        this.hideArchivedItemsSelectionChanged(node.entityType);
    }

    getTasks(): Promise<any> {
        return new Promise<any>((resolve) => {
            const handle = this.pendingNavigation?.data?.handle ? this.pendingNavigation?.data?.handle : this.pendingNavigation?.handle;
            this.restService.post(Endpoint.taskManager_getTasks, {
                taskHandles: [handle],
                includeDrDebug: true,
                includeResourceStatistics: true
            }).then(response => {
                resolve(response);
            }, error => {
                resolve({});
            });
        });
    }

    createThenNavigate = (modalComponent: any, modalData: any, objectName?: string): void => {
        this.preselectNodeCallback(() => {
            this.commonService.openModal(modalComponent, modalData).then(response => {
                if (response && response.modalData) {
                    if (objectName === 'tag') {
                        response.modalData[objectName] = Object.assign(response.modalData[objectName], { type: EntityType.tagView });
                    }
                    this.commonService.sendMessage(MessageType.navigationRequest, objectName ? response.modalData[objectName] : response.modalData);
                }
            });
        });
    }

    loadDataForEntityTypeAndSelectNode(nodeToSelect: GenericTreeNode): void {
        if (nodeToSelect) {
            let nodeToExpand = null;
            const isWorkflowStepNode = nodeToSelect.data instanceof WorkflowStep;
            let workflowParentHandle = null;
            if (isWorkflowStepNode) {
                nodeToSelect.entityType = EntityType.workFlow;
                workflowParentHandle = nodeToSelect?.parent?.data?.handle;
            }
            nodeToExpand = this.getParentNodeByEntityType(nodeToSelect.entityType);

            if (nodeToExpand?.children?.length === 0) {
                const responsePromise = this.navigationTree.loadNodeData(nodeToExpand, true, true);
                if (responsePromise) {
                    responsePromise.then(() => {
                        this.autoSelectFilteredNode(nodeToSelect, workflowParentHandle);
                    });
                }
            } else {
                this.autoSelectFilteredNode(nodeToSelect, workflowParentHandle);
            }
        }
    }

    autoSelectFilteredNode(nodeToSelect: GenericTreeNode, workflowStepParentHandle: string): void {
        if (nodeToSelect.entityType === EntityType.searchResult) {
            const node = this.navigationTree.getNodeByHandle(nodeToSelect.data.handle, SearchSearchHistoryMenu);
            if (!node) {
                // search node is not in tree, added it at top of the searches and then navigate to it
                const searchNodeToSelect = this.createSearchNode(nodeToSelect.data);
                this.navigationTree.selectNode(searchNodeToSelect);
            } else {
                this.navigationRequestHandler(node.data);
            }
        } else {
            if (workflowStepParentHandle) {
                const wfNode = this.navigationTree.getNodeByHandle(workflowStepParentHandle, WorkflowMenu);
                if (wfNode?.children?.length === 0) {
                    const responsePromise = this.navigationTree.loadNodeData(wfNode, true, true);
                    if (responsePromise) {
                        responsePromise.then(() => {
                            this.navigationRequestHandler(nodeToSelect.data);
                        });
                    }
                } else {
                    this.navigationRequestHandler(nodeToSelect.data);
                }
            } else {
                this.navigationRequestHandler(nodeToSelect.data);
            }
        }
    }

    downloadAll(entityViewHandle: string, isPdfMode = false, isStampEnabled = false): void {
        if (entityViewHandle) {
            const docName1 = isPdfMode ? 'documents' : 'natives';
            const docName2 = isPdfMode ? 'PDFs' : 'natives';
            this.commonService.openConfirmationWarningModal(`You are attempting to download all ${docName1} in this list ${isPdfMode ? 'as PDFs' : ''}. Depending on the size of the documents, this could take considerable time and/or render the browser unresponsive. Consider creating a new export stream to produce the ${docName2} directly to an export location instead.`,
                'Warning', 'Continue', 'Cancel', null, 'custom-size-message').then(result => {
                    if (result.modalResult) {
                        this.restService.post(Endpoint.documentManager_retrieveDocuments, {
                            viewHandle: entityViewHandle,
                            documentInfos: null,
                            format: isPdfMode ? DocumentFormat.PDF : DocumentFormat.NATIVE,
                            applyStamp: isStampEnabled
                        });
                        this.commonService.openInformationToastNotification(`The ${isPdfMode ? 'PDFs' : 'documents'} are being prepared for downloading. Once finished, the archive (.zip) can be downloaded from the Work Basket.`);
                    }
                });
        }
    }

    openSaveOrLoadFromTemplate(node: GenericTreeNode, operationType: TemplateOperationType): void {
        if (node) {
            this.commonService.openModal(SaveAsLoadFromTemplateComponent, {
                class: 'vertical-percent-80',
                modalData: {
                    operationType: operationType, selectedItem: node.data || node.bundle,
                    templateType: EntityType.workFlow, permissions: node.permissions,
                    isSystemContext: false,
                    currentOrg: this.commonService.currentUser?.customerName
                }
            });
        }
    }

    onContextMenuActionCallback = (node: GenericTreeNode, action: string): void => {
        if (node && action) {
            // dashboard
            if (node.automationId === DashboardMenu.automationId) {
                switch (action) {
                    case 'Hide Navigation Tree': {
                        this.resizers.first.toggleCollapse();
                        return;
                    }
                    case 'Refresh Navigation Tree': {
                        this.refreshAllTreeData();
                        return;
                    }
                    case 'Find in Navigation Tree': {
                        this.commonService.openModal(FindInTreeModalComponent, { class: 'vertical-percent-80 horizontal-px-800' }).then(modalResponse => {
                            if (modalResponse?.modalResult && modalResponse.modalData?.target) {
                                if ((modalResponse.modalData.target.entityType === EntityType.searchResult) && !(modalResponse.modalData.target.data instanceof WorkflowStep)) {
                                    const promiseList = [];
                                    if (modalResponse.modalData.target.data.task === SearchType.SEARCH_FROM_BULK_SEARCH) {
                                        SearchSearchHistoryMenu.children.forEach(node => {
                                            if (node.data?.task === SearchType.BULK) {
                                                promiseList.push(this.navigationTree.loadNodeData(node)); // Load any bulk search folders if we're looking for a bulk search
                                            }
                                        });
                                    }
                                    Promise.all(promiseList).then(() => {
                                        this.commonService.sendMessage(MessageType.navigationRequest, modalResponse.modalData.target.data);
                                    });
                                } else {
                                    this.commonService.sendMessage(MessageType.navigationRequest, modalResponse.modalData.target);
                                }
                            }
                        });
                        return;
                    }
                }
            }
            // exports
            if (node.automationId === ExportMenu.automationId) {
                if (action === 'New Export Stream ...' || action === 'New Reef Review Export Stream ...') {
                    this.createThenNavigate(ExportModalComponent, { class: 'vertical-percent-100' });
                }
                if (action === 'New Reef ECA Export Stream ...') {
                    this.createThenNavigate(ExportModalComponent, { class: 'vertical-percent-100', isDRExport: node.isReefReviewProject });
                }
                return;
            }
            // imports
            if (node.automationId === ImportMenu.automationId) {
                switch (action) {
                    case 'New Data Set ...': {
                        node.data.shortMessageFormat = false;
                        this.createThenNavigate(DataSetModalComponent, { modalData: node.data, class: 'vertical-percent-100 horizontal-percent-100' });
                        break;
                    }
                    case 'New Data Set from Load File ...': {
                        this.createThenNavigate(NewLoadFileModalComponent, { class: 'vertical-percent-100 horizontal-percent-100' });
                        return;
                    }
                    case 'New Data Set for Short Message Format ...': {
                        node.data.shortMessageFormat = true;
                        this.createThenNavigate(DataSetModalComponent, { modalData: node.data, class: 'vertical-percent-100 horizontal-percent-100' });
                        return;
                    }
                    case 'Add Shared Data Set ...': {
                        this.createThenNavigate(AddSharedDataSetModalComponent, { class: 'vertical-percent-100 horizontal-percent-100' });
                        return;
                    }
                }
            }
            // custodian
            if (node.automationId === ProjectDataCustodianMenu.automationId) {
                if (action === 'New Custodian ...') {
                    this.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Custodian({ priorityEnabled: false }) }, 'custodian');
                }
                return;
            }
            // media IDs
            if (node.automationId === ProjectDataMediaIDsMenu.automationId) {
                if (action === 'New MediaID ...') {
                    this.createThenNavigate(CreateEditEntityModalComponent, { modalData: new MediaID() }, 'mediaid');
                }
                return;
            }
            // batches
            if (node.automationId === ProjectDataBatchesMenu.automationId) {
                if (action === 'New Batch ...') {
                    this.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Batch() }, 'batch');
                }
                return;
            }
            // folders
            if (node.automationId === ProjectDataFolderMenu.automationId) {
                if (action === 'New Folder ...') {
                    this.createThenNavigate(EditBucketModalComponent, { modalData: { mode: EditMode.FOLDER } }, 'dynamicFolder');
                }
                return;
            }
            // tags
            if (node.automationId === ProjectDataTagMenu.automationId) {
                if (action === 'New Tag ...') {
                    this.createThenNavigate(EditBucketModalComponent, { modalData: { mode: EditMode.TAG } }, 'tag');
                }
                return;
            }
            // analytic
            if (node.automationId === AnalyticProfileMenu.automationId || node.automationId === AnalyticMenu.automationId
                || node.automationId === AnalyticComparisonMenu.automationId) {
                if (action === 'New Synthetic Document ...') {
                    this.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Profile() }, 'profile');
                    return;
                }
                if (action === 'New Comparison ...') {
                    this.createThenNavigate(CreateEditComparisonModalComponent,
                        { modalData: new Comparison(), class: 'vertical-percent-100 horizontal-percent-100' });
                    return;
                }
                if (action === 'Update') {
                    const requestData = {
                        projectHandle: this.commonService.currentProject.handle,
                        taskDescription: 'Update Comparison in project level'
                    };
                    this.restService.post(Endpoint.projectManager_processComparisons, requestData);
                    return;
                }
            }

            if (node.entityType === EntityType.workFlow) {
                switch (action) {
                    case 'New Workflow ...': {
                        this.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Workflow() }, 'workFlow');
                        return;
                    }
                    case 'Save to Template ...': {
                        this.openSaveOrLoadFromTemplate(node, TemplateOperationType.saveAs);
                        return;
                    }
                    case 'Load from Template ...': {
                        node.bundle = { handle: this.commonService.currentProject?.handle };
                        this.openSaveOrLoadFromTemplate(node, TemplateOperationType.loadFrom);
                        return;
                    }
                    case 'Copy ...': {
                        const nameless = new Workflow({});
                        Object.assign(nameless, node.data);
                        nameless.name = '';
                        this.createThenNavigate(CreateEditEntityModalComponent, { modalData: nameless, options: { copy: true } }, 'workFlow');
                        return;
                    }
                    case 'Lock':
                    case 'Unlock': {
                        this.isBusyMessage = action + 'ing ...';
                        this.isBusy = true;
                        this.restService.post(action === 'Lock' ? Endpoint.workFlowManager_lockWorkFlow : Endpoint.workFlowManager_unlockWorkFlow,
                            { handle: node.data.handle }).then(response => {
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                                this.isBusy = false;
                            }).catch(reason => { this.isBusy = false; });
                        return;
                    }
                }
            }

            // project data, any other children, common operations
            if (node.data && node.entityType) {
                const updateStatusType = this.commonService.entityTypeToUpdateStatusType(node.entityType);
                const entity = node.data;
                let entityView = node.data;
                if (node.data.view instanceof View || node.data instanceof SavedSearch) {
                    entityView = node.data.view;
                } else if (node.data.workingSetView instanceof View) {
                    entityView = node.data.workingSetView;
                }
                let workflowStepType;
                if (node.data instanceof WorkflowStep && node.parent && node.parent.data) {
                    const attr = node.parent.data.queryItems.items[node.data.orderOfExecution - 1].attributes.find(a => a.name === 'steptype');
                    workflowStepType = attr ? `Step ${node.data.orderOfExecution}: ${attr.value}` : undefined;
                }
                switch (action) {
                    case 'Add Tags ...': {
                        this.commonService.openModal(TagsModalComponent, {
                            modalData: <TagsModalData>{
                                targets: [entityView],
                                targetType: ModalTargetType.view,
                                operationType: TagOperationType.tag,
                                updateStatusType: updateStatusType,
                                workflowStepType: workflowStepType
                            }
                        });
                        break;
                    }
                    case 'Remove Tags ...': {
                        this.commonService.openModal(TagsModalComponent, {
                            modalData: <TagsModalData>{
                                targets: [entityView],
                                targetType: ModalTargetType.view,
                                operationType: TagOperationType.untag,
                                updateStatusType: updateStatusType,
                                workflowStepType: workflowStepType
                            }
                        });
                        break;
                    }
                    case 'Add to ...': {
                        this.commonService.openModal(AddToRemoveFromModalComponent, {
                            modalData: <AddToRemoveFromModalData>{
                                targets: [entityView],
                                operationType: SaveToRemoveFromOperationType.add,
                                targetType: ModalTargetType.view,
                            },
                            class: 'horizontal-px-700'
                        });
                        break;
                    }
                    case 'Add to Project Data': {
                        this.restService.post(Endpoint.viewManager_addFromView, {
                            filterSrcDuplicates: false,
                            includeFamilyMembers: false,
                            destViewHandle: this.commonService.projectData.handle,
                            srcViewHandle: entityView.corpusViewHandle,
                            taskDescription: 'Adding documents to Project Data',
                        }).then(response => {
                            if (response) {
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CUSTODIAN);
                            }
                        });
                        break;
                    }

                    case 'Remove from ...': {
                        this.commonService.openModal(AddToRemoveFromModalComponent, {
                            modalData: <AddToRemoveFromModalData>{
                                targets: [entityView],
                                operationType: SaveToRemoveFromOperationType.remove,
                                targetType: ModalTargetType.view,
                            },
                            class: 'horizontal-px-700'
                        });
                        break;
                    }
                    case 'Remove from Project Data': {
                        this.commonService.openModal(RemoveRestoreDocsModalComponent, {
                            modalData: <RemoveRestoreDocsModalData>{
                                targets: [entityView],
                                operationType: RemoveRestoreDocsOperationType.removeFromProjectData,
                                targetType: ModalTargetType.view,
                                updateStatusType: UpdateStatusType.WORKING_SET
                            }
                        });
                        break;
                    }

                    case 'Create Manifest ...': {
                        if (this.commonService.selectedNavigationNode && this.commonService.selectedNavigationNode.data &&
                            this.commonService.selectedNavigationNode.data.handle === entity.handle && this.commonService.selectedNavigationNode.navigationInfo.selectedTab &&
                            this.commonService.selectedNavigationNode.navigationInfo.selectedTab.path === 'document-list') {
                            this.commonService.openModal(CreateManifestModalComponent, { modalData: { entity: entityView, defaultColumns: this.currentColumnsFromDocList }, class: 'vertical-percent-100 horizontal-percent-100', backdrop: false, keyboard: false }).then((modalResponse) => {
                                if (modalResponse.modalResult && node.isArchivedNode()) {
                                    this.updateDataForArchivedNode('document-list', node);
                                }
                            });
                        } else {
                            this.commonService.openModal(CreateManifestModalComponent, { modalData: { entity: entityView, entityType: node.entityType }, class: 'vertical-percent-100 horizontal-percent-100', backdrop: false, keyboard: false }).then((modalResponse) => {
                                if (modalResponse.modalResult && node.isArchivedNode()) {
                                    this.updateDataForArchivedNode('document-list', node);
                                }
                            });
                        }
                        break;
                    }
                    case 'Create Comparison ...': {
                        this.createThenNavigate(CreateEditComparisonModalComponent, { modalData: new Comparison(), class: 'vertical-percent-100 horizontal-percent-100' });
                        break;
                    }
                    case 'Create Sample ...': {
                        this.createThenNavigate(CreateSampleModalComponent, { modalData: entity });
                        break;
                    }
                    case 'Export to Review ...': {
                        this.restService.post(Endpoint.projectManager_getExportOptionsForReview, { projectHandle: this.commonService.currentProject.handle }).then(response => {
                            if (response.exportVolumesAllowed) {
                                this.commonService.openModal(ExportModalComponent, {
                                    modalData: {
                                        entity: entity,
                                        entityView: entityView,
                                        exportVolumesAllowed: response.exportVolumesAllowed
                                    }
                                })
                            } else if (entityView) {
                                this.commonService.openModal(ExportToReviewModalComponent, {
                                    modalData: {
                                        viewDocumentCount: entity?.documentCount,
                                        entityView: entityView
                                    }
                                })
                            }
                        });

                        break;
                    }
                    case 'Create Export Comparison Report ...': {
                        this.commonService.openModal(GenericPickerModalComponent, { modalData: { entityType: 'export-comparison', items: entity } }).then(response => {
                            if (response && response.modalResult && response.modalData && response.modalData.export && response.modalData.export.data && response.modalData.export.data.handle &&
                                response.modalData.selectedTreeItem) {
                                const data = {
                                    viewHandle: response.modalData.selectedTreeItem,
                                    exportStreamHandle: response.modalData.export.data.handle,
                                    taskDescription: response.modalData.taskDescription,
                                    generateSearchTermReport: response.modalData.generateSearchTermReport,
                                    provideExactGeneratedDocSizes: response.modalData.provideExactGeneratedDocSizes
                                };
                                this.restService.post(Endpoint.groupViewManager_compareToExport, data).then((result) => {
                                    if (result) {
                                        // result is a Task; result.handle === the desired node's node.data.handle
                                        // node === result's parent node; 3rd array item is the tab to navigate to
                                        this.commonService.sendMessage(MessageType.navigationRequest, [node, result, 'reports']);
                                    }

                                });
                            }
                        });
                        break;
                    }
                    case 'Edit ...': {
                        if (entity instanceof DynamicFolder) {
                            this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.EDITFOLDER, entity: entity } });
                        } else if (entity instanceof Comparison) {
                            this.commonService.openModal(CreateEditComparisonModalComponent, { modalData: entity, class: 'vertical-percent-100 horizontal-percent-100' });
                        } else if (entity instanceof View && entity.viewType === ViewType.TAG) {
                            this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.EDITTAG, entity: entity } });
                        } else {
                            this.commonService.openModal(CreateEditEntityModalComponent, { modalData: entity });
                        }
                        break;
                    }
                    case 'Delete': {
                        if (entity.viewType === ViewType.TAG) {
                            this.commonService.tagNode = node;
                        }
                        this.commonService.openModal(DeleteEntityModalComponent, { modalData: entity }).then(response => {
                            if (entity.viewType === ViewType.TAG) {
                                if (!response.modalResult) {
                                    node.isLoading = false;
                                } else {
                                    node.isDisabled = true;
                                    node.isTagDeletion = true;
                                    this.commonService.tagNode = null;
                                    if (this.commonService.selectedNavigationNode?.parent?.name === 'Tags') {
                                        this.navigationTree.selectNode(node.parent);
                                    }
                                    this.commonService.pendingDeletionTags.push(node);
                                }
                            }
                        });
                        break;
                    }
                    case 'New Export Volume ...': {
                        if (entity instanceof ExportStream) {
                            this.commonService.openModal(ExportModalComponent, { modalData: entity, class: 'vertical-percent-100' });
                        }
                        break;
                    }
                    case 'Copy to Document Storage': {
                        if (entity instanceof Corpus) {
                            this.commonService.openModal(CopyToDocStorageModalComponent, { modalData: entity });
                        }
                        break;
                    }
                    case 'Update Metadata ...': {
                        this.commonService.openModal(UpdateMetadataModalComponent, { modalData: { docHandles: entity.handle, selectedNode: node } });
                        break;
                    }
                    case 'Share':
                    case 'Unshare': {
                        if (entity instanceof Corpus) {
                            const corpus = entity as Corpus;
                            node.isLoading = true;
                            node.isDisabled = true;
                            this.restService.post(Endpoint.corpusManager_updateCorpusData, {
                                corpusName: corpus.name,
                                description: corpus.description,
                                sharedState: corpus.sharedState === SharedState.SHARED ? SharedState.PRIVATE : SharedState.SHARED,
                                viewHandle: corpus.corpusViewHandle,
                                representationTypes: [],
                                updateContent: false
                            }).then(response => {
                                node.isLoading = false;
                                node.isDisabled = false;
                                this.commonService.openSuccessToastNotification(corpus.sharedState === SharedState.SHARED ? `${corpus.name} has been unshared` : `${corpus.name} has been shared`);
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                            }, error => {
                                node.isLoading = false;
                                node.isDisabled = false;
                            });
                        }
                        break;
                    }
                    case 'Download All as PDFs': {
                        if (entityView) {
                            this.commonService.openModal(DownloadAsPDFStampModalComponent, { class: 'horizontal-px-400' }).then(response => {
                                if (response?.modalResult && response.modalData) {
                                    this.downloadAll(entityView.handle, true, response.modalData.isStampEnabled);
                                }
                            });
                        }
                        break;
                    }
                    case 'Download All Natives': {
                        if (entityView) {
                            this.downloadAll(entityView.handle)
                        }
                        break;
                    }
                    case 'View Configuration ...': {
                        this.commonService.openModal(DataSetConfigurationModalComponent, {
                            modalData: {
                                handle: entity.handle,
                                representationType: entity.representationType
                            },
                            class: 'vertical-percent-100 horizontal-percent-100'
                        });
                        break;
                    }
                    case 'Update Patterns': {
                        const data = {
                            corpusName: entity.name, description: entity.description, sharedState: entity.sharedState,
                            viewHandle: entity.corpusViewHandle, representationTypes: [], updateContent: true
                        };

                        this.restService.post(Endpoint.corpusManager_updateCorpusData, data)
                            .then(response => {
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                            });
                        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                        break;
                    }
                    case 'Reindex ...': {
                        if (entity instanceof Corpus) {
                            this.commonService.openModal(ReindexDataSetModalComponent, { modalData: entity });
                        }
                        break;
                    }
                    case 'Add Family': {
                        const requestData = {
                            viewHandle: entityView ? entityView.handle : entity.handle,
                            taskDescription: 'Adding Family documents to ' + (entityView ? entityView.name : entity.name)
                        };
                        this.restService.post(Endpoint.viewManager_addAllFamilyMembers, requestData).then(response => {
                            if (response) {
                                this.commonService.sendMessage(MessageType.entityChanged, updateStatusType);
                            }
                        });
                        break;
                    }
                    case 'Reprocess ...': {
                        this.commonService.openModal(ReprocessModalComponent, {
                            modalData: <ReprocessModalData>{
                                selectedNode: node,
                                targetType: ModalTargetType.view,
                                targets: [entityView ? entityView.handle : entity.handle],
                                updateStatusType: updateStatusType
                            }
                        });
                        break;
                    }
                    case 'Extract Office 365 Data ...': {
                        this.commonService.openModal(ExtractOffice365DataModalComponent, {
                            modalData: <ExtractOffice365DataModalData>{
                                targetType: ModalTargetType.view,
                                targets: [entityView ? entityView.handle : entity.handle],
                                updateStatusType: updateStatusType,
                                auxParsingStatus: entityView.name
                            }
                        });
                        break;
                    }
                    case 'OCR ...': {
                        this.commonService.openModal(OcrSettingsComponent, {
                            modalData: <OCRSettingsModalData>{
                                targets: entityView,
                                viewHandle: entityView.handle,
                                targetType: ModalTargetType.view
                            },
                            class: 'horizontal-px-400'
                        });
                        break;
                    }
                    case 'Calculate Word List': {
                        if (node.entityType === EntityType.import && node.name !== 'Imports' && entity.representationType !== RepresentationSet.VECTOR_SET) {
                            this.commonService.openErrorModal('Word List can\'t be calculated because none of the target data sets are indexed at the Analytic level.').then(result => {
                                return;
                            });
                        } else {
                            const calculateMsg = 'Calculate Word List? \n\n This may take some time. You can monitor operation progress in the Work Basket.';
                            this.commonService.openConfirmationModal(calculateMsg, 'Calculate Word List', 'Calculate Word List', 'Cancel').then(result => {
                                if (result.modalResult) {
                                    const handle = entityView ? entityView.handle : entity.handle;
                                    this.restService.post(Endpoint.viewManager_createWordList, { viewHandle: handle }).then(() => {
                                        return;
                                    }, error => {
                                        console.log(error);
                                    });
                                }
                            });
                        }
                        break;
                    }
                    case 'View Word List ...': {
                        this.commonService.openModal(WordListModalComponent, {
                            modalData: { handle: entityView ? entityView.handle : entity.handle },
                            class: 'horizontal-px-1100 vertical-percent-80'
                        });
                        break;
                    }
                    case 'Search by Synthetic Document ...': {
                        this.commonService.openModal(TargetPickerModalComponent, {
                            modalData: { targetPickerTreeType: TargetPickerTreeType.searchByProfile },
                            class: 'horizontal-px-700 vertical-percent-80'
                        }).then(response => {
                            if (response && response.modalResult && response.modalData && response.modalData.target instanceof GenericTreeNode) {
                                const target = response.modalData.target as GenericTreeNode;
                                const handle = target.data && target.data.view ? target.data.view.handle : target.data.handle;
                                if (target.data instanceof WorkflowStep) {
                                    response.modalData.target.name = response.modalData.target.data.description;
                                }
                                this.restService.post(Endpoint.viewManager_searchByDoc, {
                                    attributes: [{
                                        name: 'searchbyprofile',
                                        value: entity.name
                                    }],
                                    sourceDocHandle: entity.documentHandle,
                                    viewHandleList: [handle],
                                    taskDescription: 'Search by synthetic documents ' + entity.name + ' in ' + target.name,
                                    threshold: response.modalData.threshold
                                }).then(() => {
                                    this.isSearchBusy = false;
                                });
                            }
                        });
                        break;
                    }
                    case 'Update Saved Search': {
                        const requestData = {
                            dynamicFolderHandle: entity.handle,
                            taskDescription: 'Update saved search: ' + entity.name
                        };
                        this.restService.post(Endpoint.projectManager_processActiveRule, requestData).then(response => {
                            if (response) {
                                this.commonService.sendMessage(MessageType.entityChanged, updateStatusType);
                            }
                        });
                        break;
                    }
                    case 'Update': {
                        const requestData = {
                            comparisonHandle: entityView.handle,
                            taskDescription: 'Update Comparison : ' + entityView.name
                        };
                        this.restService.post(Endpoint.comparisonManager_processComparison, requestData);
                        return;
                    }
                    case 'Copy for External Imaging ...': {
                        this.commonService.openModal(CopyToLoadFromExternalAreaModalComponent, {
                            modalData: { modalType: ExternalAreaModalType.copyFor, viewHandle: entity.handle, viewName: entity.name },
                            class: 'vertical-percent-80 horizontal-px-800'
                        });
                        break;
                    }
                    case 'Copy to External Area ...': {
                        this.commonService.openModal(CopyToLoadFromExternalAreaModalComponent, {
                            modalData: { modalType: ExternalAreaModalType.copyTo, viewHandle: entity.handle, viewName: entity.name },
                            class: 'vertical-percent-80 horizontal-px-800'
                        });
                        break;
                    }
                    case 'Load from External Area ...': {
                        this.commonService.openModal(CopyToLoadFromExternalAreaModalComponent, {
                            modalData: { modalType: ExternalAreaModalType.loadFrom, viewHandle: entity.handle, viewName: entity.name },
                            class: 'vertical-percent-80 horizontal-px-800'
                        });
                        break;
                    }
                    case 'Create Data Set from Search Results ...': {
                        this.createThenNavigate(CreateDatasetFromSearchResultModalComponent,
                            { modalData: entity });
                        break;
                    }
                    case 'Export All Staged': {
                        if (entity instanceof ExportStream) {
                            const exportStream = entity as ExportStream;
                            node.isLoading = true;
                            node.isDisabled = true;
                            this.restService.post(Endpoint.exportStreamManager_exportStream, {
                                exportStreamHandle: exportStream.handle,
                                taskDescription: `Exporting ${exportStream.name} : ${this.commonService.currentProject.name} - ${exportStream.name}`
                            }).then(response => {
                                node.isLoading = false;
                                node.isDisabled = false;
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                            }, error => {
                                node.isLoading = false;
                                node.isDisabled = false;
                                if (error && error.extendedStatus) {
                                    this.commonService.openErrorModal(error.extendedStatus);
                                }
                            });
                        }
                        break;
                    }
                    case 'Update Custodians': {
                        if (entity instanceof ExportStream) {
                            const exportStream = entity as ExportStream;
                            node.isLoading = true;
                            node.isDisabled = true;
                            this.restService.post(Endpoint.exportStreamManager_updateExportStreamCustodians, {
                                exportStreamHandle: exportStream.handle,
                                taskDescription: `Updating Custodians for ${exportStream.name}`
                            }).then(response => {
                                node.isLoading = false;
                                node.isDisabled = false;
                                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                            }, error => {
                                node.isLoading = false;
                                node.isDisabled = false;
                                if (error && error.extendedStatus) {
                                    this.commonService.openErrorModal(error.extendedStatus);
                                }
                            });
                        }
                        break;
                    }
                    case 'Export Duplicates Volume ...': {
                        if (entity instanceof ExportStream) {
                            this.commonService.openModal(ExportDuplicatesVolumeModalComponent, {
                                modalData: { exportStream: entity }
                            });
                        }
                        break;
                    }
                    case 'Create Near-Duplicate Metadata ...': {
                        if (entity instanceof ExportStream) {
                            this.commonService.openModal(CreateNearDuplicateMetadataModalComponent, {
                                modalData: { exportStream: entity }
                            });
                        }
                        break;
                    }
                    case 'Update ...': {
                        if (entity instanceof ExportVolume) {
                            this.commonService.openModal(UpdateExportVolumeModalComponent, {
                                modalData: {
                                    exportVolume: entity,
                                    exportStream: node.parent ? node.parent.data : null
                                }
                            });
                        }
                        break;
                    }
                    case 'Generate Load File ...': {
                        if (entity instanceof ExportStream) {
                            this.commonService.openModal(GenerateLoadFileModalComponent, {
                                modalData: {
                                    exportStream: entity
                                },
                                class: 'vertical-percent-80'
                            });
                        } else if (entity instanceof ExportVolume) {
                            this.commonService.openModal(GenerateLoadFileModalComponent, {
                                modalData: {
                                    exportVolume: entity,
                                    exportStream: node.parent ? node.parent.data : null
                                },
                                class: 'vertical-percent-80'
                            });
                        }
                        break;
                    }
                    case 'Save Search ...': {
                        const requestData = {
                            facetFilters: null,
                            reportRequests: [{ reportType: 'SEARCH', cachedOnly: false }],
                            viewHandle: entityView ? entityView.handle : entity.handle,
                        };
                        this.restService.post(Endpoint.viewManager_getReport, requestData).then(response => {
                            if (response && response.reports && response.reports.length > 0) {
                                const item = response.reports[0];
                                if (item.reportType === 'SEARCH' && item.attributes && item.attributes.length > 0) {
                                    const newSavedSearch = new SavedSearch();
                                    let attr = item.attributes.find(a => a.name === 'queryEntered');
                                    newSavedSearch.searchQuery = attr ? attr.value : '';
                                    attr = item.attributes.find(a => a.name === 'synonymExpansion');
                                    newSavedSearch.includeSynonyms = attr ? attr.value : false;
                                    attr = item.attributes.find(a => a.name === 'metadataExpansion');
                                    newSavedSearch.includeMetadata = attr ? attr.value : false;
                                    attr = item.attributes.find(a => a.name === 'familyExpansion');
                                    newSavedSearch.includeFamily = attr ? attr.value : false;
                                    attr = item.attributes.find(a => a.name === 'generateReport');
                                    newSavedSearch.generateReports = attr ? attr.value : false;
                                    attr = item.attributes.find(a => a.name === 'targethandle');
                                    newSavedSearch.targetViewHandle = attr ? attr.value : '';

                                    attr = item.attributes.find(a => a.name === 'filterHandles');
                                    if (attr != null && attr.value) {
                                        newSavedSearch.corporaFilters = attr.value;
                                    }
                                    this.createThenNavigate(CreateEditEntityModalComponent, { modalData: newSavedSearch });
                                }
                            }
                        });
                        break;
                    }
                    case 'Find Exact Duplicates':
                    case 'Find Content Duplicates': {
                        const dupType = action.includes('Exact') ? 'EXACT' : 'CONTENT';
                        const requestData = {
                            duplicateType: dupType,
                            taskDescription: `Find ${dupType} duplicate groups in ${entity.name}`,
                            viewHandle: entityView ? entityView.handle : entity.handle,
                        };
                        this.restService.post(Endpoint.viewManager_searchForDuplicateGroups, requestData);
                        break;
                    }
                    case 'Export':
                        {
                            if (entity instanceof ExportVolume) {
                                const exportVolume = entity as ExportVolume;
                                node.isLoading = true;
                                node.isDisabled = true;
                                const exportStreamName = node.parent.data.name;
                                this.restService.post(Endpoint.exportStreamManager_exportStream, {
                                    exportStreamHandle: exportVolume.handle,
                                    taskDescription: `Exporting ${exportStreamName} : ${this.commonService.currentProject.name} - ${exportStreamName}`
                                }).then(response => {
                                    node.isLoading = false;
                                    node.isDisabled = false;
                                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                                }, error => {
                                    node.isLoading = false;
                                    node.isDisabled = false;
                                    if (error && error.extendedStatus) {
                                        this.commonService.openErrorModal(error.extendedStatus);
                                    }
                                });
                            }
                        }
                        break;
                    case 'Exclude from Export Stream': {
                        this.updateExportvolume(node);
                        node.icon = '';
                        node.hasFadedNodeName = true;
                        node.data.exportVolumeDedupeState = ExportVolumeDedupeState.Excluded;
                        break;
                    }
                    case 'Include in Export Stream': {
                        this.updateExportvolume(node);
                        node.hasFadedNodeName = false;
                        node.icon = 'fa-check';
                        node.data.exportVolumeDedupeState = ExportVolumeDedupeState.Included;
                        break;
                    }
                    case 'Import External Images ...': {
                        this.commonService.openModal(CopyToLoadFromExternalAreaModalComponent, {
                            modalData: { modalType: ExternalAreaModalType.importExternal, viewHandle: entity.handle, viewName: entity.name },
                            class: 'vertical-percent-80 horizontal-px-1200'
                        });
                        break;
                    }
                    case 'Discard Pile Log ...': {
                        this.commonService.openModal(DiscardPileLogComponent, {
                            modalData: {},
                            class: 'horizontal-percent-100 vertical-percent-100'
                        })
                        break;
                    }
                    default: {
                        console.warn('Attempted to do an action that is not yet supported: ' + action);
                    }
                }
            }
        }
    }

    updateExportvolume(node: GenericTreeNode) {
        if (node?.entityType === EntityType.exportVolume) {
            node.isLoading = true;
            const state = node.hasFadedNodeName ? ExportVolumeDedupeState.Included : ExportVolumeDedupeState.Excluded;
            const taskDescription = `Changing state to ${state} for ${node.name} volume`;
            this.restService.post(Endpoint.exportStreamManager_updateExportVolume, { dedupeState: state, handle: node.data.handle, taskDescription: taskDescription }).then(response => {
                node.isLoading = false;
            }, () => {
                node.isLoading = false;
            });
        }
    }

    getParentNodeByEntityType(entityType: EntityType): GenericTreeNode {
        switch (entityType) {
            case EntityType.import: return ImportMenu;
            case EntityType.export: return ExportMenu;
            case EntityType.custodian:
            case EntityType.folder:
            case EntityType.tagView:
                return ProjectDataMenu;
            case EntityType.searchResult: return SearchSearchHistoryMenu;
            case EntityType.savedSearch: return SearchSavedSearchMenu;
            case EntityType.workFlow: return WorkflowMenu;
            case EntityType.profile: return AnalyticProfileMenu;
            case EntityType.comparison: return AnalyticComparisonMenu;
            case EntityType.discardPile: return DiscardPileMenu;
        }
        return null;
    }

    checkboxSelectionChangedCallback = (selectedNodes: Array<GenericTreeNode>, node: GenericTreeNode) => {
        // TODO: currently this function does not need any implementation after the change export volumes no longer has checekboxes (ECASL-39007)
        // Leaving this skeleton method as is for now - can be used for any future implementation. 
    }

    hideArchivedItemsSelectionChanged(entityType?: EntityType): void {
        const moreButton = this.navigationTree.getNodeBy(n => n.entityType === entityType && n.isButton);
        if (moreButton && entityType === EntityType.searchResult) {
            moreButton.name = this.hideArchivedItems ? this.customMoreButtonLabel : this.commonService.moreButtonOriginalLabel;
        } else if (moreButton && entityType === EntityType.import) {
            moreButton.name = this.hideArchivedItems ? this.customMoreButtonLabel : this.commonService.moreDataAreasButtonOriginalLabel;
        }
        this.navigationTree.treeDataUpdated();
    }

    tabsBarVisible(): boolean {
        if (this.selectedNode && this.selectedNode.data && this.selectedNode.data.task === 'BULK_SEARCH') {
            return false;
        }

        return Boolean(this.selectedNode && this.selectedNode.navigationInfo && this.selectedNode.navigationInfo.tabs &&
            (this.selectedNode.navigationInfo.tabs.length > 1 || this.selectedNode.navigationInfo.tabs[0].name));
    }

    helpButtonVisible(): boolean {
        return Boolean(this.currentComponent && this.currentComponent.helpIndex && (!this.currentComponent.headerTemplate || this.selectedNode.entityType === EntityType.profileHistory));
    }

    openHelp = () => {
        if (this.currentComponent && this.currentComponent.helpIndex) {
            this.commonService.openHelp(this.currentComponent.helpIndex);
        }
    }

    selectedComparisonTypeChanged = (item: CustomSelectItem, restoreDocViewerTab = false, skipURLChange = false) => {
        if (item) {
            this.selectedDropdownListComparison = item;
            if (this.selectedNode && this.selectedNode.data) {
                this.selectedNode.data.docListHandle = this.selectedDropdownListComparison.data;
                if (this.commonService.selectedComparisonNavigation) {
                    this.commonService.selectedComparisonNavigation.docListHandle = this.selectedDropdownListComparison.data;
                }
                if (this.commonService.selectedNavigationNode?.data) {
                    this.commonService.selectedNavigationNode.data.docListHandle = this.selectedDropdownListComparison.data;
                }
                // reload doc list for new handle
                if (this.selectedNode.navigationInfo.selectedTab?.path === 'cluster-list') {
                    this.commonService.sendMessage(MessageType.refreshCluster);
                    if (!skipURLChange) {
                        this.navigateTo(this.selectedNode.navigationInfo.tabs.filter(t => t.path === 'cluster-list')[0]);
                    }
                } else {
                    if (restoreDocViewerTab && this.selectedNode.navigationInfo.selectedTab && !skipURLChange) {
                        this.navigateTo(this.selectedNode.navigationInfo.selectedTab);
                    } else if (!skipURLChange) {
                        this.navigateTo(this.selectedNode.navigationInfo.tabs.filter(t => t.default)[0]);
                    }
                }
                this.commonService.currentClusterView = null;
            }
        }
    }

    editComparison(): void {
        this.commonService.openModal(CreateEditComparisonModalComponent, { modalData: this.selectedNode.data, class: 'vertical-percent-100 horizontal-percent-100' });
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
        this.refreshTreeNodesEvent.unsubscribe();
        this.navigationRequestEvent.unsubscribe();
        if (!this.refreshBulkSearchParentNodeEvent.closed) {
            this.refreshBulkSearchParentNodeEvent.unsubscribe();
        }
        this.tabNavigationRequestEvent.unsubscribe();
        this.currentColumnsEvent.unsubscribe();
        this.clearPendingNavigationEvent.unsubscribe();
        this.searchTypeChangedEvent.unsubscribe();
        this.componentActivateEvent?.unsubscribe();
        this.treeDataService.ngOnDestroy();

        if (this.commonService.projectName) {
            this.restService.post(Endpoint.projectManager_closeProject, { handle: this.commonService.projectName });
        }

        (<any>window).navigateTo = undefined;
    }
}
